/*
  v2-rail.js — サイト共通の横スクロール / スライダー（Splide・slick・Swiper の置き換え）
  ライブラリを使わず、ブラウザ標準の横スクロール + scroll-snap に最小限の操作だけ足す。

  ■ 使い方（マークアップだけで動く）
    <div class="v2-rail" data-v2-rail="cards" data-v2-rail-nav data-v2-rail-progress>
      <div class="v2-rail__track" data-v2-rail-track>
        <div class="v2-rail__slide">…</div> …
      </div>
    </div>

    data-v2-rail           "cards"（カードを並べて流す）/ "slides"（1 枚ずつ。KV・バナー）
    data-v2-rail-nav       前後ボタンを付ける
    data-v2-rail-progress  下にゲージ（ドラッグ・クリックで移動できる）
    data-v2-rail-dots      ドット（slides 向け）
    data-v2-rail-autoplay  自動再生の間隔（ms）。OS のモーション低減設定では止める
    data-v2-rail-loop      端から先頭へ回り込む（slides 向け。前後 1 枚ずつ複製を置く）
    data-v2-rail-rewind    最後の次は先頭へ戻る（複製は置かない。製品画像など）
    data-v2-rail-toggle    自動再生の一時停止ボタン要素のセレクタ（ドット列の末尾に移す）
    data-v2-rail-duration  ボタン・ドットで送るときの移動時間（ms、既定 200。0 で瞬時に切り替え。自動再生は 450 以上）

    既存の Splide のマークアップ（.splide > .splide__track > .splide__list > .splide__slide）にも
    data-v2-rail を付ければそのまま動く（クラスは Splide 互換のものも付けるので既存の見た目を流用できる）。

  ■ JS から
    V2Rail.create(el, opts) → api { update, go(i), next(), prev(), index(), destroy() }
    V2Rail.init(root)       root 以下の [data-v2-rail] をまとめて起動（DOMContentLoaded で document に対して自動実行）
    el.addEventListener('v2rail:change', e => e.detail.index)
*/
(function () {
  'use strict';
  if (window.V2Rail) return;

  var reduce = function () { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); };
  var has = function (el, name) { return el.hasAttribute('data-v2-rail-' + name); };
  var attr = function (el, name) { return el.getAttribute('data-v2-rail-' + name); };

  function create(el, opts) {
    if (el.v2rail) return el.v2rail;
    opts = opts || {};
    var mode = opts.mode || el.getAttribute('data-v2-rail') || 'cards';
    var track = opts.track || el.querySelector('[data-v2-rail-track]') || el.querySelector('.splide__track') || el;
    var list = opts.list || (track.querySelector(':scope > .splide__list') || track);
    var slideSel = opts.slides || ':scope > *';
    var gutterEl = opts.gutter || null;
    var navOn = opts.nav != null ? opts.nav : has(el, 'nav');
    var progOn = opts.progress != null ? opts.progress : has(el, 'progress');
    var dotsOn = opts.dots != null ? opts.dots : has(el, 'dots');
    var loop = opts.loop != null ? opts.loop : has(el, 'loop');
    var rewind = opts.rewind != null ? opts.rewind : has(el, 'rewind');
    var interval = opts.autoplay != null ? opts.autoplay : parseInt(attr(el, 'autoplay') || '0', 10);
    if (reduce()) interval = 0;

    el.classList.add('v2-rail', 'is-native', 'v2-rail--' + mode);
    track.classList.add('v2-rail__track');
    if (list !== track) list.classList.add('v2-rail__list');

    function realSlides() {
      return Array.prototype.filter.call(list.querySelectorAll(slideSel), function (s) {
        return !s.hasAttribute('data-v2-rail-clone') && !s.classList.contains('is-grade-last');
      });
    }
    var slides = realSlides();
    slides.forEach(function (s) { s.classList.add('v2-rail__slide'); });

    /* ── ループ: 前後に 1 枚ずつ複製（見切れて見える隣の 1 枚ぶん） ── */
    var clones = [];
    if (loop && slides.length > 1) {
      var mk = function (src) {
        var c = src.cloneNode(true);
        c.setAttribute('data-v2-rail-clone', '');
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('inert', '');
        /* 複製の画像は、端に少し見えていても最初は読まない（LCP の画像と帯域を取り合う）。warm() か load 後に戻す */
        c.querySelectorAll('img, source').forEach(function (i) {
          ['src', 'srcset'].forEach(function (k) {
            var v = i.getAttribute(k);
            if (!v) return;
            if (!i.hasAttribute('data-v2-' + k)) i.setAttribute('data-v2-' + k, v);
            if (k === 'srcset' || !/^data:/.test(v)) i.removeAttribute(k);
          });
          i.removeAttribute('fetchpriority');
        });
        return c;
      };
      var head = mk(slides[slides.length - 1]);
      var tail = mk(slides[0]);
      list.insertBefore(head, slides[0]);
      list.appendChild(tail);
      clones = [head, tail];
    }
    var warmed = false;
    function warm() {
      if (warmed) return;
      warmed = true;
      clones.forEach(function (c) {
        c.querySelectorAll('[data-v2-src], [data-v2-srcset]').forEach(function (i) {
          ['srcset', 'src'].forEach(function (k) { var v = i.getAttribute('data-v2-' + k); if (v) { i.setAttribute(k, v); i.removeAttribute('data-v2-' + k); } });
        });
      });
    }
    if (clones.length) { if (document.readyState === 'complete') window.setTimeout(warm, 0); else window.addEventListener('load', warm, { once: true }); }

    /* ── 位置（レイアウトの読み取りは幅や中身が変わったときにまとめて 1 回。スクロール中は読まない） ── */
    var geo = null;
    function measure() {
      var pad = mode === 'slides' ? 0 : (parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0);
      var cw = track.clientWidth;
      var items = Array.prototype.filter.call(list.querySelectorAll(slideSel), function (s) {
        return !s.classList.contains('is-grade-last') && s.offsetParent !== null;
      }).map(function (s) {
        var l = s.offsetLeft, w = s.offsetWidth;
        return { el: s, l: l, w: w, at: mode === 'slides' ? l - (cw - w) / 2 : l - pad };
      });
      geo = { cw: cw, pad: parseFloat(getComputedStyle(track).paddingLeft) || 0, items: items, sw: track.scrollWidth };
      return geo;
    }
    function G() { return geo || measure(); }
    function max() { var g = G(); return g.sw - g.cw; }
    function slideLeft(s) {
      var it = G().items.filter(function (x) { return x.el === s; })[0];
      if (it) return it.at;
      if (mode === 'slides') return s.offsetLeft - (track.clientWidth - s.offsetWidth) / 2;
      return s.offsetLeft - (parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0);
    }
    function allSlides() { return G().items.map(function (x) { return x.el; }); }
    function nearest() {
      var best = null, bd = Infinity, sl = track.scrollLeft;
      G().items.forEach(function (x) { var d = Math.abs(x.at - sl); if (d < bd) { bd = d; best = x.el; } });
      return best;
    }
    if ('MutationObserver' in window) new MutationObserver(function () { geo = null; }).observe(list, { childList: true });
    function realIndexOf(s) {
      if (!s) return 0;
      if (s === clones[0]) return slides.length - 1;
      if (s === clones[1]) return 0;
      var i = slides.indexOf(s);
      return i < 0 ? 0 : i;
    }
    function jump(left) {
      /* スナップを一瞬外して瞬間移動（ループの付け替え用） */
      var st = track.style.scrollSnapType;
      track.style.scrollSnapType = 'none';
      track.scrollLeft = left;
      void track.offsetWidth;
      track.style.scrollSnapType = st;
    }
    /* ── ボタン・ドット・自動再生での移動は、距離に関係なく一定の短い時間で（10/16）
       ブラウザ標準の smooth は距離に比例して長く、PC で 1 枚（約 1300px）送るのに 0.6 秒かかっていた。
       移動中はスナップを外して scrollLeft を直接動かし、着いたら戻す（行き先は先にスナップ位置へ丸めておく）。
       指・ホイールで触ったらその場で止めてブラウザに任せる */
    var DUR = opts.duration != null ? opts.duration : parseInt(attr(el, 'duration') || '200', 10);
    var anim = 0, animSnap = '', animBehavior = '';
    function animating() { return !!anim; }
    function stopAnim() {
      if (!anim) return;
      window.cancelAnimationFrame(anim);
      anim = 0;
      track.style.scrollSnapType = animSnap;
      track.style.scrollBehavior = animBehavior;
    }
    function animateTo(left, dur) {
      stopAnim();
      var from = track.scrollLeft, d = left - from;
      if (Math.abs(d) < 1) return;
      if (!dur || reduce()) { jump(left); return; }
      animSnap = track.style.scrollSnapType;
      animBehavior = track.style.scrollBehavior;
      track.style.scrollSnapType = 'none';
      track.style.scrollBehavior = 'auto';
      var t0 = null;
      function step(now) {
        if (t0 == null) t0 = now;
        var p = Math.min(1, (now - t0) / dur);
        var e = 1 - Math.pow(1 - p, 3); /* ease-out: 出だしで大きく動き、静かに止まる */
        track.scrollLeft = from + d * e;
        if (p < 1) { anim = window.requestAnimationFrame(step); return; }
        anim = 0;
        track.scrollLeft = left;
        track.style.scrollSnapType = animSnap;
        track.style.scrollBehavior = animBehavior;
        settled();
      }
      anim = window.requestAnimationFrame(step);
    }
    function nearestSnap(left) {
      var best = left, bd = Infinity;
      snapPoints().forEach(function (p) { var dd = Math.abs(p - left); if (dd < bd) { bd = dd; best = p; } });
      return best;
    }
    ['pointerdown', 'wheel', 'touchstart'].forEach(function (t) {
      track.addEventListener(t, stopAnim, { passive: true });
    });
    function scrollToLeft(left, instant, dur) {
      left = Math.max(0, Math.min(max(), left));
      if (instant || reduce()) { stopAnim(); jump(left); return; }
      animateTo(nearestSnap(left), dur != null ? dur : DUR);
    }
    var cur = 0;
    function go(i, instant, dur) {
      var n = slides.length;
      if (!n) return;
      var target;
      if (loop && n > 1) {
        if (i >= n) target = clones[1];
        else if (i < 0) target = clones[0];
        else target = slides[i];
      } else {
        i = Math.max(0, Math.min(n - 1, i));
        target = slides[i];
      }
      scrollToLeft(slideLeft(target), instant, dur);
    }
    function next() {
      if (mode === 'cards') return scrollToLeft(track.scrollLeft + Math.round(track.clientWidth * 0.8));
      if (rewind && !loop && cur >= slides.length - 1) return go(0);
      go(cur + 1);
    }
    function prev() {
      if (mode === 'cards') return scrollToLeft(track.scrollLeft - Math.round(track.clientWidth * 0.8));
      if (rewind && !loop && cur <= 0) return go(slides.length - 1);
      go(cur - 1);
    }

    /* ── 前後ボタン（ページに .splide__arrows があればそれを使う。opts.splideArrows なら Splide と同じ形で作る） ── */
    var nav = null, prevB = null, nextB = null;
    var ownArrows = Array.prototype.filter.call(el.querySelectorAll('.splide__arrows'), function (x) {
      /* 入れ子のスライダーの矢印は拾わない */
      var host = x.parentElement.closest('.splide, [data-v2-rail], .js-future-list-splide');
      return x.parentElement === el || host === el;
    })[0];
    if (navOn && ownArrows && ownArrows.querySelector('.splide__arrow--prev')) {
      nav = ownArrows;
      nav.classList.add('v2-rail-nav', 'is-own');
      prevB = nav.querySelector('.splide__arrow--prev'); nextB = nav.querySelector('.splide__arrow--next');
      prevB.setAttribute('data-rail-prev', ''); nextB.setAttribute('data-rail-next', '');
      nav.addEventListener('click', function (e) {
        var b = e.target.closest('button'); if (!b) return;
        stopAuto(true);
        if (b === nextB) next(); else if (b === prevB) prev();
      });
    } else if (navOn) {
      nav = document.createElement('div');
      if (opts.splideArrows) {
        var svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40" focusable="false"><path d="m15.5 0.932-4.3 4.38 14.5 14.6-14.5 14.5 4.3 4.4 14.6-14.6 4.4-4.3-4.4-4.4-14.6-14.6z"></path></svg>';
        nav.className = 'splide__arrows v2-rail-nav is-own';
        nav.innerHTML = '<button type="button" class="splide__arrow splide__arrow--prev" data-rail-prev aria-label="前へ">' + svg + '</button><button type="button" class="splide__arrow splide__arrow--next" data-rail-next aria-label="次へ">' + svg + '</button>';
      } else {
        nav.className = 'v2-rail-nav';
        nav.innerHTML = '<button type="button" data-rail-prev aria-label="前へ"></button><button type="button" data-rail-next aria-label="次へ"></button>';
      }
      prevB = nav.firstElementChild; nextB = nav.lastElementChild;
      nav.addEventListener('click', function (e) {
        var b = e.target.closest('button'); if (!b) return;
        stopAuto(true);
        if (b === nextB) next(); else prev();
      });
      (opts.navAfter ? el.parentNode.insertBefore(nav, el.nextSibling) : el.appendChild(nav));
    }

    /* ── ゲージ ── */
    var prog = null, thumb = null;
    if (progOn) {
      prog = document.createElement('div');
      prog.className = 'v2-rail-progress';
      prog.innerHTML = '<i></i>';
      thumb = prog.firstElementChild;
      (nav && opts.navAfter ? nav : el).insertAdjacentElement('afterend', prog);
    }

    /* ── ドット（Splide 互換のクラスも付けて既存の見た目を流用） ── */
    var dots = null, dotBtns = [];
    if (dotsOn && slides.length > 1) {
      dots = document.createElement('ul');
      dots.className = 'v2-rail-dots splide__pagination';
      dots.setAttribute('role', 'tablist');
      slides.forEach(function (s, i) {
        var li = document.createElement('li');
        li.setAttribute('role', 'presentation');
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'v2-rail-dot splide__pagination__page';
        b.setAttribute('aria-label', (i + 1) + ' 枚目へ');
        b.addEventListener('click', function () { stopAuto(true); go(i); });
        li.appendChild(b); dots.appendChild(li); dotBtns.push(b);
      });
      el.appendChild(dots);
      var tg = opts.toggle || (attr(el, 'toggle') && el.querySelector(attr(el, 'toggle')));
      if (tg) {
        if (interval) {
          var tli = document.createElement('li');
          tli.className = 'mv-autoplay-toggle-item v2-rail-toggle-item';
          tli.setAttribute('role', 'presentation');
          tli.appendChild(tg);
          dots.appendChild(tli);
          tg.addEventListener('click', function () { if (paused) { paused = false; startAuto(); } else { paused = true; stopAuto(); } syncToggle(); });
        } else {
          tg.remove();
          tg = null;
        }
      }
    }
    function syncToggle() {
      if (!tg) return;
      tg.classList.toggle('is-paused', paused);
      tg.setAttribute('aria-label', paused ? 'スライドの自動再生を再開' : 'スライドの自動再生を一時停止');
    }

    /* ── スクロールに追従 ── */
    var ticking = false, settleTimer = null;
    function update() {
      ticking = false;
      var m = max();
      var off = m <= 0 || track.offsetParent === null;
      if (prog) {
        var vis = m > 0 ? track.clientWidth / track.scrollWidth : 1;
        var pos = m > 0 ? track.scrollLeft / m : 0;
        thumb.style.width = (vis * 100) + '%';
        thumb.style.transform = 'translateX(' + Math.round(pos * (prog.clientWidth - thumb.clientWidth)) + 'px)';
        prog.hidden = off;
      }
      if (nav) {
        nav.hidden = off;
        prevB.disabled = !loop && !rewind && track.scrollLeft <= 1;
        nextB.disabled = !loop && !rewind && track.scrollLeft >= m - 1;
      }
      /* Splide 互換: 画面に収まっているスライドに is-visible（はみ出したものを薄くするページの CSS 用。fromSplide のときだけ） */
      if (opts.visibleClass) {
        var g = G(), sl0 = track.scrollLeft;
        g.items.forEach(function (x) {
          var v = x.l - sl0 >= g.pad - 2 && x.l + x.w - sl0 <= g.cw + 2;
          if (x.vis !== v) { x.vis = v; x.el.classList.toggle('is-visible', v); }
        });
      }
      var ni = realIndexOf(nearest());
      if (ni !== cur) {
        cur = ni;
        dotBtns.forEach(function (b, i) { b.classList.toggle('is-active', i === cur); b.setAttribute('aria-selected', i === cur ? 'true' : 'false'); });
        allSlides().forEach(function (s) { s.classList.toggle('is-active', s === slides[cur]); });
        el.dispatchEvent(new CustomEvent('v2rail:change', { detail: { index: cur } }));
      }
    }
    function settled() {
      /* ループ: 複製の上で止まったら本物へ付け替える（移動の途中では付け替えない。着いたら animateTo が呼ぶ） */
      if (!loop || !clones.length || animating()) return;
      var s = nearest();
      if (s === clones[0] || s === clones[1]) jump(slideLeft(slides[realIndexOf(s)]));
    }
    function onScroll() {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(settled, 140);
    }
    track.addEventListener('scroll', onScroll, { passive: true });
    if ('onscrollend' in window) track.addEventListener('scrollend', function () { window.clearTimeout(settleTimer); settled(); });

    /* ── マウスのドラッグ（タッチはブラウザ標準のまま） ── */
    var drag = null, dragged = false, snapT = null;
    function snapPoints() {
      var m = max(), pts = allSlides().map(function (s) { return Math.max(0, Math.min(m, slideLeft(s))); });
      if (mode === 'cards') pts.push(m);
      return pts;
    }
    function release() { window.clearTimeout(snapT); el.classList.remove('is-dragging'); }
    function settle(vel) {
      var pos = Math.max(0, Math.min(max(), track.scrollLeft + vel * 120));
      var best = 0, bd = Infinity;
      snapPoints().forEach(function (p) { var d = Math.abs(p - pos); if (d < bd) { bd = d; best = p; } });
      if (Math.abs(best - track.scrollLeft) < 1) { release(); return; }
      animateTo(best, DUR);
      track.addEventListener('scrollend', release, { once: true });
      snapT = window.setTimeout(release, 800);
    }
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      window.clearTimeout(snapT);
      drag = { x: e.clientX, sl: track.scrollLeft, lx: e.clientX, lt: e.timeStamp, v: 0, cur: cur };
      dragged = false;
    });
    track.addEventListener('pointermove', function (e) {
      if (!drag) return;
      var dx = e.clientX - drag.x;
      if (!dragged) {
        if (Math.abs(dx) < 4) return;
        dragged = true;
        stopAuto(true);
        el.classList.add('is-dragging');
        try { track.setPointerCapture(e.pointerId); } catch (err) {}
      }
      var dt = e.timeStamp - drag.lt;
      if (dt > 0) drag.v = (drag.lx - e.clientX) / dt;
      drag.lx = e.clientX; drag.lt = e.timeStamp;
      track.scrollLeft = drag.sl - dx;
      e.preventDefault();
    });
    function endDrag(e) {
      if (!drag) return;
      var v = drag.v, moved = drag.x - (e && e.clientX != null ? e.clientX : drag.lx), from = drag.cur;
      drag = null;
      if (!dragged) return;
      window.setTimeout(function () { dragged = false; }, 0);
      if (mode === 'slides') {
        /* 1 枚ずつ: 50px 以上か、勢いよく払ったら隣の 1 枚へ。足りなければ元の 1 枚に戻す。
           10/8: 吸い付き（scroll-snap）は移動し終わってから戻す。先に戻すと、放した瞬間に元の 1 枚へ吸い付いて
           「一度右に戻ってから左へ流れる」動きになっていた */
        var dir = Math.abs(moved) > 50 || Math.abs(v) > 0.4 ? (moved > 0 ? 1 : -1) : 0;
        var to = from + dir;
        if (!loop && !rewind) to = Math.max(0, Math.min(slides.length - 1, to));
        else if (!loop && rewind) to = (to + slides.length) % slides.length;
        go(to);
        track.addEventListener('scrollend', release, { once: true });
        snapT = window.setTimeout(release, 900);
        return;
      }
      settle(v);
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('click', function (e) { if (dragged) { e.preventDefault(); e.stopPropagation(); } }, true);
    track.addEventListener('dragstart', function (e) { if (drag || e.target.closest('a, img')) e.preventDefault(); });
    /* タッチで触ったら自動再生はいったん止め、離して少ししたら再開 */
    track.addEventListener('touchstart', function () { stopAuto(true); }, { passive: true });

    /* ゲージのドラッグ */
    if (prog) {
      var pd = null;
      prog.addEventListener('pointerdown', function (e) {
        if (e.button !== 0) return;
        var m = max(); if (m <= 0) return;
        var r = prog.getBoundingClientRect(), tr = thumb.getBoundingClientRect();
        var ratio = m / Math.max(1, r.width - tr.width);
        el.classList.add('is-dragging');
        if (e.clientX < tr.left || e.clientX > tr.right) track.scrollLeft = Math.max(0, Math.min(m, (e.clientX - r.left - tr.width / 2) * ratio));
        pd = { x: e.clientX, sl: track.scrollLeft, ratio: ratio };
        prog.setPointerCapture(e.pointerId);
        e.preventDefault();
      });
      prog.addEventListener('pointermove', function (e) { if (pd) track.scrollLeft = pd.sl + (e.clientX - pd.x) * pd.ratio; });
      var endP = function () { if (!pd) return; pd = null; settle(0); };
      prog.addEventListener('pointerup', endP);
      prog.addEventListener('pointercancel', endP);
    }

    /* キーボード */
    track.setAttribute('tabindex', '0');
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); stopAuto(true); next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); stopAuto(true); prev(); }
    });

    /* ── 自動再生（見えていない・タブが裏・一時停止中は止める） ── */
    /* ゲージは CSS アニメーション（.is-autoplaying の間だけ、アクティブなドットが interval かけて伸びる）。JS は毎フレーム動かさない */
    var paused = false, visible = true, raf = 0, resumeT = null;
    if (interval) el.style.setProperty('--v2-rail-interval', interval + 'ms');
    function tick() {
      raf = 0;
      /* 自動再生は手で送るより少しゆっくり（勝手に動くものは急に動かさない） */
      go(cur + 1, false, Math.max(DUR, 450));
      raf = window.setTimeout(tick, interval);
    }
    function startAuto() {
      if (!interval || paused || !visible || document.hidden || raf) return;
      el.classList.add('is-autoplaying');
      raf = window.setTimeout(tick, interval);
    }
    function stopAuto(resume) {
      if (raf) { window.clearTimeout(raf); raf = 0; }
      el.classList.remove('is-autoplaying');
      window.clearTimeout(resumeT);
      if (resume && interval && !paused) resumeT = window.setTimeout(startAuto, 2500);
    }
    if (interval) {
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (ents) { visible = ents[0].isIntersecting; if (visible) startAuto(); else stopAuto(); }).observe(el);
      }
      document.addEventListener('visibilitychange', function () { if (document.hidden) stopAuto(); else startAuto(); });
      startAuto();
    }
    syncToggle && tg && syncToggle();

    /* ── 初期位置・幅の変化 ── */
    function refresh() {
      geo = null;
      if (gutterEl) {
        var r = gutterEl.getBoundingClientRect();
        el.style.setProperty('--rail-gutter', Math.max(0, Math.round(r.left)) + 'px');
        el.style.setProperty('--rail-gutter-r', Math.max(0, Math.round(document.documentElement.clientWidth - r.right)) + 'px');
      }
      if (mode === 'slides' && slides[cur]) jump(slideLeft(slides[cur]));
      update();
    }
    var ro = 'ResizeObserver' in window ? new ResizeObserver(function () { window.requestAnimationFrame(refresh); }) : null;
    if (ro) ro.observe(track); else window.addEventListener('resize', refresh);
    cur = -1;
    if (mode === 'slides') jump(slideLeft(slides[0] || list));
    update();
    if (mode === 'slides') el.classList.add('is-initialized', 'is-rendered', 'is-active');

    var api = {
      el: el,
      update: refresh,
      go: function (i) { stopAuto(true); go(i); },
      next: next,
      prev: prev,
      index: function () { return cur; },
      warm: warm,
      play: function () { paused = false; startAuto(); syncToggle(); },
      pause: function () { paused = true; stopAuto(); syncToggle(); },
      destroy: function () {
        stopAuto();
        if (ro) ro.disconnect();
        track.removeEventListener('scroll', onScroll);
        clones.forEach(function (c) { c.remove(); });
        [nav, prog, dots].forEach(function (x) { if (x) x.remove(); });
        el.classList.remove('is-native', 'is-initialized', 'is-rendered', 'is-dragging');
        track.removeAttribute('tabindex');
        el.v2rail = null;
      }
    };
    el.v2rail = api;
    return api;
  }

  /* ── Splide の設定をそのまま渡して動かす（移行用）。
     new Splide(sel, opts).mount() → V2Rail.fromSplide(sel, opts)
     対応: type(loop/slide/fade) perPage(小数可) fixedWidth autoWidth gap padding arrows pagination autoplay interval rewind
           breakpoints（Splide と同じ max-width。destroy: true でその幅では止めてページの CSS に任せる） ── */
  var idSeq = 0;
  function px(v) { return v == null || v === false ? null : (typeof v === 'number' ? v + 'px' : String(v)); }
  function fromSplide(target, soIn, extra) {
    extra = extra || {};
    var els = typeof target === 'string' ? document.querySelectorAll(target) : (target && target.length != null ? target : [target]);
    var apis = [];
    Array.prototype.forEach.call(els, function (el) {
      if (!el || el.v2rail) { if (el && el.v2rail) apis.push(el.v2rail); return; }
      var so = soIn || {};
      /* Splide と同じく、要素の data-splide（JSON）は渡された設定より優先 */
      var dso = null;
      try { dso = el.getAttribute('data-splide') ? JSON.parse(el.getAttribute('data-splide')) : null; } catch (e) {}
      if (dso) {
        var merged = {};
        Object.keys(so).forEach(function (k) { merged[k] = so[k]; });
        Object.keys(dso).forEach(function (k) { if (k !== 'breakpoints') merged[k] = dso[k]; });
        var bp = {};
        [so.breakpoints || {}, dso.breakpoints || {}].forEach(function (b) { Object.keys(b).forEach(function (w) { bp[w] = bp[w] || {}; Object.keys(b[w]).forEach(function (k) { bp[w][k] = b[w][k]; }); }); });
        merged.breakpoints = bp;
        so = merged;
      }
      /* 画像の遅延読み込みは data-splide-lazy → 標準の loading=lazy に */
      el.querySelectorAll('img[data-splide-lazy]').forEach(function (img) {
        var src = img.getAttribute('data-splide-lazy');
        if (!img.getAttribute('src') || img.getAttribute('src') === '') img.setAttribute('src', src);
        if (img.getAttribute('data-splide-lazy-srcset') && !img.getAttribute('srcset')) img.setAttribute('srcset', img.getAttribute('data-splide-lazy-srcset'));
        if (!img.hasAttribute('loading')) img.loading = 'lazy';
        img.classList.add('is-loaded');
      });
      if (!el.id) { idSeq++; el.id = 'splide' + (idSeq < 10 ? '0' : '') + idSeq; }
      el.querySelectorAll('.splide__slide').forEach(function (sl, i) { if (!sl.id) sl.id = el.id + '-slide' + (i < 9 ? '0' : '') + (i + 1); });
      var bps = Object.keys(so.breakpoints || {}).map(Number).sort(function (a, b) { return b - a; });
      function conf() {
        var c = {};
        Object.keys(so).forEach(function (k) { if (k !== 'breakpoints') c[k] = so[k]; });
        bps.forEach(function (w) { if (window.matchMedia('(max-width: ' + w + 'px)').matches) Object.keys(so.breakpoints[w]).forEach(function (k) { c[k] = so.breakpoints[w][k]; }); });
        return c;
      }
      var mode = so.type === 'loop' || so.type === 'fade' ? 'slides' : 'cards';
      if (mode === 'cards' && (so.perPage || 1) === 1 && !so.fixedWidth && !so.autoWidth) mode = 'slides';
      var inst = null;
      function style(c) {
        var st = el.style;
        ['--v2-rail-w', '--v2-rail-per', '--v2-rail-gap', '--v2-rail-pad'].forEach(function (v) { st.removeProperty(v); });
        el.classList.remove('has-per', 'v2-rail--peek', 'v2-rail--noarrows', 'v2-rail--nodots');
        if (c.fixedWidth) st.setProperty('--v2-rail-w', px(c.fixedWidth));
        else if (c.autoWidth) st.setProperty('--v2-rail-w', 'max-content');
        st.setProperty('--v2-rail-gap', px(c.gap) || '0px');
        if (!c.fixedWidth && !c.autoWidth && (mode === 'cards' || (c.perPage && c.perPage !== 1))) {
          /* n 枚: 親の幅から 1 枚の幅を割り出す（トラックの見た目上の広げ方に左右されない） */
          var per = c.perPage || 1, g = parseFloat(px(c.gap)) || 0;
          var w = (el.clientWidth - (per - 1) * g) / per;
          if (w > 0) st.setProperty('--v2-rail-w', (Math.floor(w * 100) / 100) + 'px');
        }
        var pad = c.padding && typeof c.padding === 'object' ? (c.padding.left != null ? c.padding.left : c.padding.right) : c.padding;
        if (pad) { st.setProperty('--v2-rail-pad', px(pad)); if (mode === 'slides') el.classList.add('v2-rail--peek'); }
        if (c.arrows === false) el.classList.add('v2-rail--noarrows');
        if (c.pagination === false) el.classList.add('v2-rail--nodots');
      }
      function apply() {
        var c = conf();
        style(c);
        if (c.destroy) { if (inst) { inst.destroy(); inst = null; el.classList.remove('v2-rail', 'v2-rail--' + mode); } return; }
        if (!inst) {
          inst = create(el, {
            mode: mode,
            loop: so.type === 'loop',
            rewind: !!so.rewind,
            nav: so.arrows !== false || bps.some(function (w) { return so.breakpoints[w].arrows; }),
            splideArrows: true,
            dots: !!so.pagination,
            autoplay: so.autoplay ? (so.interval || 5000) : 0,
            toggle: extra.toggle || null,
            visibleClass: true,
            slides: '.splide__slide'
          });
        } else inst.update();
      }
      apply();
      var rt = null;
      window.addEventListener('resize', function () { window.clearTimeout(rt); rt = window.setTimeout(apply, 120); });
      bps.forEach(function (w) { var mq = window.matchMedia('(max-width: ' + w + 'px)'); (mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply)); });
      var readyFns = [], mounted = false;
      var proxy = {
        root: el,
        mount: function () {
          if (!mounted) { mounted = true; window.setTimeout(function () { readyFns.splice(0).forEach(function (f) { f(); }); }, 0); }
          return proxy;
        },
        /* Splide のイベント名のうち、よく使うものだけ対応 */
        on: function (evs, fn) {
          String(evs).split(/\s+/).forEach(function (ev) {
            if (ev === 'mounted' || ev === 'ready') { if (mounted) window.setTimeout(fn, 0); else readyFns.push(fn); }
            else if (ev === 'move' || ev === 'moved' || ev === 'active') el.addEventListener('v2rail:change', function (e) { fn(e.detail.index); });
            else if (ev === 'resized' || ev === 'resize') { var t = null; window.addEventListener('resize', function () { window.clearTimeout(t); t = window.setTimeout(fn, 100); }); }
          });
          return proxy;
        },
        go: function (i) { if (inst) inst.go(typeof i === 'number' ? i : 0); },
        refresh: function () { if (inst) inst.update(); },
        destroy: function () { if (inst) inst.destroy(); inst = null; },
        get index() { return inst ? inst.index() : 0; },
        rail: function () { return inst; }
      };
      el.splide = proxy;
      apis.push(proxy);
    });
    return apis.length === 1 ? apis[0] : { mount: function () { return this; }, list: apis };
  }

  function init(root) {
    (root || document).querySelectorAll('[data-v2-rail]').forEach(function (el) { if (!el.v2rail) create(el); });
  }

  window.V2Rail = { create: create, init: init, fromSplide: fromSplide };
  /* new Splide(sel, opts) の置き換え。移行済みページ（window.__v2RailPage）か Splide が無いときは V2Rail で動かす */
  window.v2Splide = function (sel, opts, extra) {
    if (window.__v2RailPage || !window.Splide) return fromSplide(sel, opts, extra);
    return new window.Splide(sel, opts);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
})();
